import type { DetailsFormState } from './RequestDetails';
import type { YourInformationFormState } from './RequestYourInformation';
import { INCENTIVE, type PaymentRequestTypeValue, SCHOLARSHIP } from './paymentRequestTypes';
import type { ExistingUploadedDocumentsByDocumentId, UploadedDocuments } from './UploadRequiredDocuments';
import type {
  AwardPaymentRequestContentInitialState,
  AwardPaymentRequestPayloadDocument,
  AwardPaymentRequestStatus,
  AwardPaymentUploadedDocumentEntry,
  ExistingAwardPaymentRequestPayloadDocument,
} from './AwardPaymentRequestContent.types';

type AwardPaymentRequestPayload = {
  item: {
    status: AwardPaymentRequestStatus;
    id: number;
    user_id: number;
    first_name: string;
    last_name: string;
    enrolled_in_school: string;
    schoolInfo: {
      current_major: string;
      date_of_enrollment: string;
      planned_graduation_date: string;
      gpa: string;
    };
    employment_status: string;
    employmentInfo: {
      place_of_employment: string;
      industry_trade: string;
      start_date: string;
      pay_schedule: string;
      average_hours_worked_per_week: string;
      position_title: string;
    };
    payment_recipient: string;
    amount_requested: string;
    purpose: string;
    award_type: string;
    notes: string;
    last_date_of_pay_stubs: string;
    documents: Array<ExistingAwardPaymentRequestPayloadDocument | AwardPaymentRequestPayloadDocument>;
  };
};

const ALLOWED_DOCUMENT_IDS_BY_AWARD_TYPE: Record<PaymentRequestTypeValue, ReadonlySet<string>> = {
  [SCHOLARSHIP]: new Set(['registered-class', 'financial-statement', 'fafsa', 'grades-gpa']),
  [INCENTIVE]: new Set(['pay-stub']),
};

const getAllowedDocumentIdsForAwardType = (awardType?: string): ReadonlySet<string> =>
  awardType === INCENTIVE
    ? ALLOWED_DOCUMENT_IDS_BY_AWARD_TYPE[INCENTIVE]
    : ALLOWED_DOCUMENT_IDS_BY_AWARD_TYPE[SCHOLARSHIP];

const mapExistingDocuments = (
  allowedDocumentIds: ReadonlySet<string>,
  existingUploadedDocuments?: ExistingUploadedDocumentsByDocumentId
): ExistingAwardPaymentRequestPayloadDocument[] => {
  if (!existingUploadedDocuments) {
    return [];
  }

  return Object.entries(existingUploadedDocuments).flatMap(([documentId, documents]) =>
    !allowedDocumentIds.has(documentId)
      ? []
      :
    documents.map((document) => ({
      id: document.id,
      document_id: documentId,
      original_file_name: document.originalFileName,
      content_type: document.contentType,
      file_size_bytes: document.fileSizeBytes,
      is_already_persisted: true,
    }))
  );
};

const mapUploadedDocuments = (
  allowedDocumentIds: ReadonlySet<string>,
  uploadedFiles?: UploadedDocuments
): {
  metadata: AwardPaymentRequestPayloadDocument[];
  entries: AwardPaymentUploadedDocumentEntry[];
} => {
  if (!uploadedFiles) {
    return { metadata: [], entries: [] };
  }

  return Object.entries(uploadedFiles).reduce<{
    metadata: AwardPaymentRequestPayloadDocument[];
    entries: AwardPaymentUploadedDocumentEntry[];
  }>(
    (accumulator, [documentId, files]) => {
      if (!allowedDocumentIds.has(documentId)) {
        return accumulator;
      }

      files.forEach((file) => {
        accumulator.metadata.push({
          document_id: documentId,
          original_file_name: file.name,
          content_type: file.type,
          file_size_bytes: file.size,
          is_already_persisted: false,
        });

        accumulator.entries.push({
          document_id: documentId,
          file,
        });
      });

      return accumulator;
    },
    { metadata: [], entries: [] }
  );
};

export function buildAwardPaymentRequestPayload(
  yourInformationState: YourInformationFormState,
  detailsState: DetailsFormState,
  status: AwardPaymentRequestStatus,
  initialFormState?: AwardPaymentRequestContentInitialState | null
): AwardPaymentRequestPayload;

export function buildAwardPaymentRequestPayload(
  yourInformationState: YourInformationFormState,
  detailsState: DetailsFormState,
  status: AwardPaymentRequestStatus,
  initialFormState: AwardPaymentRequestContentInitialState | null | undefined,
  uploadedFiles: undefined,
  existingUploadedDocuments?: ExistingUploadedDocumentsByDocumentId
): AwardPaymentRequestPayload;

export function buildAwardPaymentRequestPayload(
  yourInformationState: YourInformationFormState,
  detailsState: DetailsFormState,
  status: AwardPaymentRequestStatus,
  initialFormState: AwardPaymentRequestContentInitialState | null | undefined,
  uploadedFiles: UploadedDocuments,
  existingUploadedDocuments?: ExistingUploadedDocumentsByDocumentId
): AwardPaymentRequestPayload | FormData;

export function buildAwardPaymentRequestPayload(
  yourInformationState: YourInformationFormState,
  detailsState: DetailsFormState,
  status: AwardPaymentRequestStatus,
  initialFormState?: AwardPaymentRequestContentInitialState | null,
  uploadedFiles?: UploadedDocuments,
  existingUploadedDocuments?: ExistingUploadedDocumentsByDocumentId
): AwardPaymentRequestPayload | FormData {
  const schoolInfo = yourInformationState.schoolInfo || {};
  const employmentInfo = yourInformationState.employmentInfo || {};
  const allowedDocumentIds = getAllowedDocumentIdsForAwardType(detailsState.awardType);

  const existingDocumentsMetadata = mapExistingDocuments(allowedDocumentIds, existingUploadedDocuments);
  const { metadata: uploadedDocumentsMetadata, entries: uploadedDocumentEntries } = mapUploadedDocuments(allowedDocumentIds, uploadedFiles);

  const documentsMetadata = [...existingDocumentsMetadata, ...uploadedDocumentsMetadata];

  const itemData = {
    status,
    id: initialFormState?.id || 0,
    user_id: initialFormState?.userId ?? yourInformationState.userId ?? 0,
    first_name: yourInformationState.firstName,
    last_name: yourInformationState.lastName,
    enrolled_in_school: yourInformationState.enrolledInSchool || '',
    schoolInfo: {
      current_major: schoolInfo.currentMajor || '',
      date_of_enrollment: schoolInfo.dateOfEnrollment || '',
      planned_graduation_date: schoolInfo.plannedGraduationDate || '',
      gpa: schoolInfo.gpa || '',
    },
    employment_status: yourInformationState.employmentStatus || '',
    employmentInfo: {
      place_of_employment: employmentInfo.placeOfEmployment || '',
      industry_trade: employmentInfo.industryTrade || '',
      start_date: employmentInfo.startDate || '',
      pay_schedule: employmentInfo.paySchedule || '',
      average_hours_worked_per_week: employmentInfo.averageHoursWorkedPerWeek || '',
      position_title: employmentInfo.positionTitle || '',
    },
    payment_recipient: detailsState.paymentRecipient,
    amount_requested: detailsState.amountRequested,
    purpose: detailsState.purpose,
    award_type: detailsState.awardType,
    notes: detailsState.notes,
    last_date_of_pay_stubs: detailsState.lastDateOfPayStubs,
    documents: documentsMetadata,
  };

  // If there are files to upload, use FormData
  if (uploadedDocumentEntries.length > 0) {
    const formData = new FormData();

    // Add all form fields as JSON
    formData.append('item', JSON.stringify(itemData));

    // Add each newly uploaded file with array-style key: document[index]
    uploadedDocumentEntries.forEach(({ file }, index) => {
      const metadataIndex = existingDocumentsMetadata.length + index;
      formData.append(`document[${metadataIndex}]`, file);
    });

    return formData;
  }

  // Return regular JSON payload for API
  return {
    item: itemData,
  };
}
